import { BehaviorSubject } from 'rxjs';
import { Account, ContactUserData, User } from '../types';
import { useState } from 'react';
import {
  getAccounts as apiGetAccounts,
  getAccountById as apiGetAccountById,
  getAdminAccounts as apiGetAdminAccounts
} from '../Api/Admin/Accounts';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { fetchContacts, fetchSfUsersApi } from '../Api/Admin/PortalUsers/fetchContacts';
import { addError } from './Error';
import {
  AccountCustomizationData,
  defaultAccountCustomizationData,
  getAccountCustomizationsById
} from './AccountCustomization';
import { getUsersByAccount } from './PortalUser';
import { getAccountHistoryApi } from '../Api/Admin/Accounts/getAccountHistoryApi';

export interface UseAccountsReturn {
  fetchAccounts: (updateAccountData?: boolean) => Promise<void>;
  fetchAdminAccounts: (filters?: ApiAdvancedFilter) => Promise<void>;
  getAccountContacts: (id: string, accountIds: string[]) => Promise<void>;
  getSfUsers: () => Promise<void>;
  accountsLoading: boolean;
}

export interface AccountData {
  id: string;
  name: string;
  accountCustomizations: AccountCustomizationData;
  sub_accounts: number;
  users: number;
  isSubAgent: boolean;
  last_updated: string;
}

export interface AccountHistory {
  account_id: string;
  action: string;
  created_at: string;
  updated_at: string;
  event_data: string;
  user: string;
  id: number;
  impersonator?: User;
  impersonated?: User;
}

export const defaultAccount: Account = {
  id: '',
  name: '',
  isSubAgent: false
};

const defaultAccountData: Account[] = [];
const defaultAccountContactData: Record<string, ContactUserData[]> = {};
const defaultSfUserData: ContactUserData[] = [];

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const accountSubject = new BehaviorSubject<Account[]>(defaultAccountData);
const adminAccountSubject = new BehaviorSubject<PaginatedResponse<Account>>({} as PaginatedResponse<Account>);
const accountDataSubject = new BehaviorSubject<PaginatedResponse<AccountData>>({} as PaginatedResponse<AccountData>);
const accountHistorySubject = new BehaviorSubject<AccountHistory[]>([]);
const loadingSubject = new BehaviorSubject<boolean>(false);
const accountContactsSubject = new BehaviorSubject<Record<string, ContactUserData[]>>(defaultAccountContactData);
const sfUserSubject = new BehaviorSubject<ContactUserData[]>(defaultSfUserData);
const emitErrorState = (message?: string): void => addError('account', message);

export const getAccountSubject = (): BehaviorSubject<Account[]> => accountSubject;
export const getAdminAccountSubject = (): BehaviorSubject<PaginatedResponse<Account>> => adminAccountSubject;
export const getAccountLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;
export const getAccountDataSubject = (): BehaviorSubject<PaginatedResponse<AccountData>> => accountDataSubject;
export const getAccountHistorySubject = (): BehaviorSubject<AccountHistory[]> => accountHistorySubject;

export const getAccountContactSubject = (): BehaviorSubject<Record<string, ContactUserData[]>> =>
  accountContactsSubject;
export const getSfUsersSubject = (): BehaviorSubject<ContactUserData[]> => sfUserSubject;
export const getAccounts = (): Account[] => accountSubject.value;

export const getAccountName = async (id: string) => {
  const account = await getAccountByIdOrSfId(id);
  return account?.name || '';
};

export const getAccountIdBySfId = async (sfId: string): Promise<string> => {
  const account = await getAccountByIdOrSfId(sfId);
  return account?.id || '';
};

export type AccountSfIdMiss = 'lookup_failed' | 'no_sf_record';

// A miss stays undefined, never '': a user save would pass an empty id on to Salesforce as the contact's account.
export const getAccountSfId = async (id: string | undefined): Promise<{ sfId?: string; miss?: AccountSfIdMiss }> => {
  if (!id) return {};
  const { account, notFound } = await lookUpAccount(id);
  if (account?.sfId) return { sfId: account.sfId };
  // A 404 is the API saying it holds no such account, which asking again won't change; a failed request might.
  return { miss: account || notFound ? 'no_sf_record' : 'lookup_failed' };
};

export const getAccountByIdOrSfId = async (id: string): Promise<Account | undefined> =>
  (await lookUpAccount(id)).account;

const lookUpAccount = async (id: string): Promise<{ account?: Account; notFound?: boolean }> => {
  // The accounts route without an id answers with the whole directory, not with nothing.
  if (!id) return {};
  const accounts = accountSubject.getValue();
  if (accounts.length > 0) {
    const account = accounts.find((acc) => acc.id === id || acc.sfId === id);
    if (account) return { account };
  }

  const { data, error } = await apiGetAccountById(id);
  if (data) {
    accountSubject.next([...accountSubject.getValue(), data]);
    return { account: data };
  }
  const notFound = !!error?.message.includes('404');
  if (error) emitErrorState(notFound ? 'Account not found' : error.message);
  return { notFound };
};

export const fetchAccountHistory = async (account_id: string) => {
  const { data, error } = await getAccountHistoryApi(account_id);
  if (data) {
    const formattedData = data.map((d) => {
      const user = d?.impersonator || d?.impersonated;
      d.user = `${user?.first_name} ${user?.last_name}`;
      return d;
    });

    accountHistorySubject.next(formattedData);
  }
  if (error) emitErrorState(error.message);
};

export const buildAccountData = () => {
  const adminAccounts = getAdminAccountSubject().value;

  const data = (adminAccounts?.data || []).map((acc) => {
    const customizations = getAccountCustomizationsById(acc.id) || defaultAccountCustomizationData(acc?.id, acc?.name);
    return {
      id: acc.id,
      name: acc.name,
      accountCustomizations: customizations,
      sub_accounts: customizations?.content?.sub_accounts?.length || 0,
      isSubAgent: acc.isSubAgent,
      users: getUsersByAccount(acc.id).length,
      last_updated: customizations?.updated_at
    } as AccountData;
  });

  // React skips a state update to the object it already holds, so a mutated-in-place value never renders.
  accountDataSubject.next({
    data,
    page: adminAccounts?.page,
    pageSize: adminAccounts?.pageSize,
    totalCount: adminAccounts?.totalCount,
    totalPages: adminAccounts?.totalPages
  } as PaginatedResponse<AccountData>);
};

export const useAccounts = (): UseAccountsReturn => {
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);

  const fetchAccounts = async (): Promise<void> => {
    loadingSubject.next(true);
    const { data, error } = await apiGetAccounts();

    if (data) {
      emitErrorState();
      accountSubject.next(data);
    }

    if (error) emitErrorState(error.message);
    loadingSubject.next(false);
  };

  const fetchAdminAccounts = async (filters?: ApiAdvancedFilter): Promise<void> => {
    const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
    inboundFetchNumberSubject.next(newFetchNumber);
    loadingSubject.next(true);
    const { data, error } = await apiGetAdminAccounts(filters);
    const currentFetchNumber = inboundFetchNumberSubject.getValue();
    if (data && newFetchNumber === currentFetchNumber) {
      emitErrorState();
      adminAccountSubject.next(data);
    }

    if (error) emitErrorState(error.message);
    newFetchNumber === currentFetchNumber && loadingSubject.next(false);
  };

  const getAccountContacts = async (id: string, accountIds: string[]) => {
    setAccountsLoading(true);
    const { data, error } = await fetchContacts(accountIds);
    if (data) {
      const currData = accountContactsSubject.getValue();
      accountContactsSubject.next({ ...currData, [id]: data });
    }
    if (error) emitErrorState(error.message);
    setAccountsLoading(false);
  };

  const getSfUsers = async () => {
    const { data, error } = await fetchSfUsersApi();
    if (data) sfUserSubject.next(data);
    if (error) emitErrorState(error.message);
  };

  return {
    fetchAccounts,
    fetchAdminAccounts,
    getAccountContacts,
    getSfUsers,
    accountsLoading
  };
};
